import { useMemo } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { uploadFileToSignedURL } from '@/hooks/useFiles';
import { useMy } from '@/hooks/useMy';
import { useAuth } from '@/state/auth';
import { CustomerTypeValue } from '@/types';
import { useMutation } from '@/utils/api';

export function useCustomerType(doNotImpersonate = false) {
  const query = useMy(
    {
      resource: 'configuration',
      query: 'customer_type',
      allTenants: true,
    },
    {
      doNotImpersonate,
    }
  );

  // Transform the data to map email -> customer type value
  const dataMap = useMemo(() => {
    const map = new Map();
    query.data?.forEach(item => {
      // Extract email from the key if username is not present
      let email = item.username;

      if (!email && item.key) {
        // Key format: #configuration#customer_type#email@example.com
        const keyParts = item.key.split('#');
        // Find the part that looks like an email
        email = keyParts.find((part: string) => part.includes('@'));
      }

      if (email && item.value) {
        map.set(email, item.value);
      }
    });

    return map;
  }, [query.data]);

  return {
    ...query,
    dataMap,
  };
}

export function useUpdateCustomerType() {
  const axios = useAxios();
  const { getToken } = useAuth();
  const { invalidate: invalidateCustomerType } = useCustomerType(true);

  return useMutation({
    mutationFn: async ({
      email,
      customerType,
    }: {
      email: string;
      customerType: CustomerTypeValue;
    }) => {
      const token = await getToken();
      await axios({
        method: 'put',
        url: '/configuration',
        data: {
          name: 'customer_type',
          value: customerType,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          Account: email,
        },
      });
      return { updatedType: customerType, email };
    },
    defaultErrorMessage: 'Failed to update customer type',
    onSuccess: (data, _variables) => {
      // Only invalidate the customer type query, not accounts
      // This prevents the dashboard from losing data during the update
      invalidateCustomerType();

      // Return updated data for optimistic updates
      return data;
    },
    onError: (error: unknown, variables) => {
      console.error(
        `Failed to update customer type for ${variables.email}:`,
        error
      );
    },
  });
}

interface CreateCustomer {
  username: string;
  password: string;
  settings_display_name?: string;
  customer_type?: CustomerTypeValue;
  profile_picture?: ArrayBuffer | null;
  enabled_flags?: string[];
  scan_level?: string;
  plextrac_id?: string;
  collaborators?: { email: string; role: string }[];
  parent_account?: string;
  allowed_domains?: string[];
  bounty_catalog_handle?: string;
  /** Operational weekly scan cadence written to #setting#schedule by onboard.go. dayOfWeek: 0-6 (WeekDay). */
  scan_cadence?: { frequency: 'weekly'; dayOfWeek: number };
  /**
   * Subscription window (YYYY-MM-DD) known at creation time. onboard.go persists
   * it and pre-freezes the account when startDate is in the future, so no
   * seed/preseed scan runs before the subscription begins. The full subscription
   * blob is still written post-onboard via updateSubscription (same window).
   */
  subscription?: { startDate: string; endDate?: string };
}

export function useCreateCustomer() {
  const axios = useAxios({ doNotImpersonate: true });
  const { invalidate: invalidateAccounts } = useMy({
    resource: 'account',
  });

  const { invalidate: invalidateCustomerType } = useCustomerType(true);

  return useMutation({
    defaultErrorMessage: 'Failed to create customer',
    mutationFn: async (payload: CreateCustomer) => {
      const profile_picture = payload.profile_picture
        ? URL.createObjectURL(new Blob([payload.profile_picture]))
        : null;

      try {
        const promise = axios.post('/customer/onboard', {
          ...payload,
          profile_picture,
        });

        toast.promise(promise, {
          loading: 'Creating New Customer...',
          success: 'New Customer created',
          error: err => {
            // Extract detailed error message from response
            let errorMessage = 'Unknown error';

            if (err.response?.data) {
              // Backend returns error message as plain text in response.data
              if (typeof err.response.data === 'string') {
                errorMessage = err.response.data;
              } else if (err.response.data.error) {
                // Structured error format
                errorMessage = err.response.data.error;
              } else if (err.response.data.message) {
                // Alternative error format
                errorMessage = err.response.data.message;
              }
            } else if (err.message) {
              // Fallback to generic error message
              errorMessage = err.message;
            }

            // Extract just the meaningful part from verbose AWS errors
            // Format: "operation error ... ExceptionName: Actual message"
            // Just get everything after the last colon
            const lastColonIndex = errorMessage.lastIndexOf(': ');
            if (lastColonIndex !== -1) {
              errorMessage = errorMessage.substring(lastColonIndex + 2).trim();
            }

            return errorMessage;
          },
        });

        const response = await promise;

        invalidateAccounts();
        invalidateCustomerType();

        if (payload.profile_picture && response.data.profile_picture_url) {
          await uploadFileToSignedURL(
            response.data.profile_picture_url,
            payload.profile_picture
          );
        }

        return response;
      } finally {
        // Revoke the blob URL to prevent memory leak
        if (profile_picture) {
          URL.revokeObjectURL(profile_picture);
        }
      }
    },
  });
}
